import {
  useCallback, useState,
} from 'react';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import {
  addRatingComment, type AddRatingCommentRequest, createRating, type CreateRatingRequest, dismissRating,
  type RatingRequestOptions, type RatingResponse,
} from './rating.repository';

const requireClientId = (clientId: number | null, action: string): number => {
  if (isNullsy(clientId)) {
    throw new Error(`Cannot ${action} when clientId is null.`);
  }

  return clientId;
};

export const useRating = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clientId = useClientIdSelector();

  const submitRating = useCallback(async (payload: CreateRatingRequest, options?: RatingRequestOptions): Promise<RatingResponse> => {
    const id = requireClientId(clientId, 'create rating');

    setIsSubmitting(true);

    return createRating(id, payload, options)
      .then(response => response.data)
      .finally(() => setIsSubmitting(false));
  }, [clientId]);

  const submitRatingComment = useCallback(async (
    ratingId: number, payload: AddRatingCommentRequest
  ): Promise<RatingResponse> => {
    const id = requireClientId(clientId, 'add rating comment');

    setIsSubmitting(true);

    return addRatingComment(id, ratingId, payload)
      .then(response => response.data)
      .finally(() => setIsSubmitting(false));
  }, [clientId]);

  const dismissRatingModal = useCallback(async (): Promise<void> => {
    const id = requireClientId(clientId, 'dismiss rating');

    return dismissRating(id);
  }, [clientId]);

  return {
    isSubmitting,
    submitRating,
    submitRatingComment,
    dismissRatingModal,
  };
};
